<template>
    <div class="trends-chart">
        <VJoyFilterBar class="trends-chart__filter-bar">
            <VJoyDropdown>
                <template #dropdown-button>
                    <VJoyFilterBarButton value="categories" label="Categories">
                        <template v-if="selectedCategories.length">
                            <VJoyTag value="values" size="xsmall" :label="selectedCategories.length.toString()" />
                        </template>
                    </VJoyFilterBarButton>
                </template>
                <template #dropdown-items>
                    <VJoyCheckboxGroup v-model:value="selectedCategories" :options="categoriesOptions">
                        <template #checkbox="item">
                            <span class="trends-chart__category-dot" :style="{backgroundColor: (item as any).color.fill}" />
                            {{ item.label }}
                        </template>
                    </VJoyCheckboxGroup>
                </template>
            </VJoyDropdown>
        </VJoyFilterBar>
        <div class="trends-chart__canvas-wrapper">
            <canvas ref="chartRef" />
        </div>
        <div v-if="isFilterEnabled" class="trends-chart__filters">
            <VJoyTag
                v-for="(category, index) in categoriesSorted"
                :key="category.id"
                variant="secondary"
                :selected="categoryVisibility[category.id]"
                :strikethrough="!categoryVisibility[category.id]"
                :data-chart-category="category"
                :data-chart-category-index="index"
                selectable
                @click="toggleCategory(category.id)"
            >
                <span class="trends-chart__category-dot" :style="{backgroundColor: category.color.fill}" />
                {{ category.label }}
            </VJoyTag>
        </div>
        <TrendsTooltip :axis-x="props.axisX" @close:after="handleCloseTooltip" />
    </div>
</template>
<script setup lang="ts">
    import {computed, onMounted, onUnmounted, ref, shallowRef, useTemplateRef, type ShallowRef} from 'vue';
    import type {ActiveElement, ChartConfiguration, ChartDataset, ChartEvent} from 'chart.js';
    import {Chart} from 'chart.js/auto';
    import {getRelativePosition} from 'chart.js/helpers';
    import ChartDataLabels, {type Context} from 'chartjs-plugin-datalabels';
    import {useLocale} from '#imports';
    import {VJoyDropdown, VJoyFilterBar, VJoyFilterBarButton, VJoyCheckboxGroup, VJoyTag} from '@maltjoy/core-vue';
    import {useTrendsChartTooltip} from '~/composables/trends/useTrendsChartTooltip';
    import type {
        TrendsDemandQuadrantNumericKey,
        TrendsDataDemandQuadrant,
        TrendsDemandQuadrantSkill,
        TrendsDemandQuadrantCategory,
    } from '~/types/TrendsData';
    import TrendsTooltip from './TrendsTooltip.vue';
    import type {ScatterPoint, TrendsChartCategory} from '../../../types/TrendsChart';
    import {referenceLinesPlugin} from '../../../utils/chartjsReferenceLines';

    const quadrantDataModules = import.meta.glob<{default: TrendsDataDemandQuadrant}>('~/assets/data/*.json', {eager: true});

    function resolveQuadrantData(dataKey: string): TrendsDataDemandQuadrant | null {
        const suffix = `${dataKey}.json`;
        for (const [path, mod] of Object.entries(quadrantDataModules)) {
            if (path.endsWith(suffix)) {
                return mod.default;
            }
        }
        return null;
    }

    type AxisDefinition = {
        label: string;
        key: TrendsDemandQuadrantNumericKey;
    };

    const props = defineProps<{
        data: string;
        axisX: AxisDefinition;
        axisY: AxisDefinition;
    }>();

    const {datasets, categoriesSorted} = useChartData(props.data, props.axisX, props.axisY);
    const {chart, handleCloseTooltip} = useChartJs(datasets, categoriesSorted);
    const {categoryVisibility, toggleCategory, categoriesOptions, isFilterEnabled, selectedCategories} = useFilters(categoriesSorted, chart);

    function useChartData(dataKey: string, axisX: AxisDefinition, axisY: AxisDefinition) {
        const {languageCode} = useLocale();

        let categoriesSorted: TrendsChartCategory[] = [];
        let datasets: ChartDataset<'scatter', ScatterPoint[]>[] = [];

        const typedData = resolveQuadrantData(dataKey);

        if (typedData && typedData.skills && typedData.categories) {
            const skillsByCategory = new Map<string, TrendsDemandQuadrantSkill[]>();
            for (const item of typedData.skills) {
                const list = skillsByCategory.get(item.category);
                if (list) {
                    list.push(item);
                } else {
                    skillsByCategory.set(item.category, [item]);
                }
            }

            categoriesSorted = typedData.categories
                .filter((category) => (skillsByCategory.get(category.id)?.length ?? 0) > 0)
                .map((category) => ({
                    id: category.id,
                    label: Object.prototype.hasOwnProperty.call(category, languageCode.value)
                        ? (category[languageCode.value as keyof TrendsDemandQuadrantCategory] as string)
                        : category.en,
                    color: {fill: category.fill_color, border: category.stroke_color},
                }))
                .sort((a, b) => a.label.localeCompare(b.label));

            datasets = categoriesSorted.map((category): ChartDataset<'scatter', ScatterPoint[]> => {
                const items = skillsByCategory.get(category.id) ?? [];
                const highestX = items.reduce((max, item) => Math.max(max, item[axisX.key]), 0);
                const highestY = items.reduce((max, item) => Math.max(max, item[axisY.key]), 0);
                const data: ScatterPoint[] = items.map((item) => ({
                    x: item[axisX.key],
                    y: item[axisY.key],
                    displayName: item.display_name,
                    displayDescription: Object.prototype.hasOwnProperty.call(item, languageCode.value)
                        ? (item[languageCode.value as keyof TrendsDemandQuadrantSkill] as string)
                        : item.en,
                    isCategoryHighestX: item[axisX.key] === highestX,
                    isCategoryHighestY: item[axisY.key] === highestY,
                    backgroundColor: category.color.fill,
                    borderColor: category.color.border,
                }));

                return {
                    label: category.label,
                    data,
                    backgroundColor: category.color.fill,
                    borderColor: category.color.border,
                    borderWidth: 1,
                };
            });
        }

        return {
            // a-z
            categoriesSorted,
            datasets,
        };
    }

    function useChartJs(_datasets: ChartDataset<'scatter', ScatterPoint[]>[], _categoriesSorted: TrendsChartCategory[]) {
        const chart = shallowRef<Chart<'scatter'> | null>(null);
        const chartRef = useTemplateRef<HTMLCanvasElement>('chartRef');
        const {commitTooltipForElement, clearTooltip} = useTrendsChartTooltip();

        const isTooltipLockedByClick = ref(false);
        const isTooltipLockeByClickOnLabel = ref(false);
        const isLabelHovered = ref(false);

        const hasActiveElement = ref(false);

        type TooltipHandlerType = 'click' | 'hover' | 'labelClick' | 'labelEnter' | 'labelLeave';

        function tooltipHandler(type: TooltipHandlerType, activeElement: ActiveElement | undefined, mouseEvent: MouseEvent, chartInstance: Chart) {
            if (['labelEnter', 'labelLeave'].includes(type)) {
                isLabelHovered.value = type === 'labelEnter';
            }

            if (isTooltipLockedByClick.value || isTooltipLockeByClickOnLabel.value) {
                if (['hover', 'labelEnter', 'labelLeave'].includes(type)) {
                    return;
                }

                if (isTooltipLockeByClickOnLabel.value && type === 'click' && !activeElement) {
                    return;
                }
            }

            if (['hover'].includes(type) && isLabelHovered.value) {
                return;
            }

            if (activeElement) {
                if (type === 'click') {
                    isTooltipLockedByClick.value = true;
                }
                if (type === 'labelClick') {
                    isTooltipLockeByClickOnLabel.value = true;
                }
            }

            if (!activeElement) {
                handleCloseTooltip();
                return;
            }

            const point = chartInstance.data.datasets[activeElement.datasetIndex]?.data[activeElement.index] as ScatterPoint;

            commitTooltipForElement({
                datasetIndex: activeElement.datasetIndex,
                dataIndex: activeElement.index,
                point,
                mouseEvent,
            });
        }

        function getActiveElementFromEvent(
            event: ChartEvent,
            elements: ActiveElement[],
            chartInstance: Chart,
        ): {activeElement: ActiveElement | undefined} {
            if (elements.length === 0) {
                hasActiveElement.value = false;
                return {activeElement: undefined};
            }

            const element: ActiveElement = elements[0];
            const {x: mouseX, y: mouseY} = getRelativePosition(event, chart.value!);
            const distance = Math.hypot(mouseX - element.element.x, mouseY - element.element.y);

            if (distance > 15) {
                hasActiveElement.value = false;
                return {activeElement: undefined};
            }

            const point = chartInstance.data.datasets[element.datasetIndex]?.data[element.index] as ScatterPoint | undefined;
            if (!point) {
                hasActiveElement.value = false;
                return {activeElement: undefined};
            }

            hasActiveElement.value = true;

            return {
                activeElement: element,
            };
        }

        function cursorHandler(type: TooltipHandlerType, activeElement: ActiveElement | undefined) {
            if (type === 'hover' && isLabelHovered.value) {
                return;
            }
            if (activeElement) {
                chart.value!.canvas.style.cursor = 'pointer';
            } else {
                chart.value!.canvas.style.cursor = 'default';
            }
        }

        const chartConfig: ChartConfiguration<'scatter'> = {
            type: 'scatter',
            data: {
                datasets,
            },
            plugins: [ChartDataLabels, referenceLinesPlugin],
            options: {
                maintainAspectRatio: false,
                elements: {
                    point: {
                        radius: 4,
                        hoverBorderWidth: 2,
                        hoverRadius: 4,
                    },
                },
                interaction: {
                    mode: 'nearest',
                    intersect: false,
                },
                onClick(event: ChartEvent, elements: ActiveElement[], chartInstance: Chart) {
                    const {activeElement} = getActiveElementFromEvent(event, elements, chartInstance);
                    const mouseEvent = event.native as MouseEvent;
                    tooltipHandler('click', activeElement, mouseEvent, chartInstance);
                },
                onHover(event: ChartEvent, elements: ActiveElement[], chartInstance: Chart) {
                    const {activeElement} = getActiveElementFromEvent(event, elements, chartInstance);
                    const mouseEvent = event.native as MouseEvent;
                    tooltipHandler('hover', activeElement, mouseEvent, chartInstance);
                    cursorHandler('hover', activeElement);
                },
                scales: {
                    x: {
                        type: 'logarithmic',
                        title: {display: true, text: props.axisX.label + ' →', font: {family: 'Moderat', weight: 'bold', size: 13}},
                        border: {
                            display: false,
                        },
                        grid: {
                            display: false,
                        },
                        ticks: {
                            display: false,
                        },
                        offset: true,
                    },
                    y: {
                        type: 'logarithmic',
                        title: {display: true, text: props.axisY.label + ' →', font: {family: 'Moderat', weight: 'bold', size: 13}},
                        border: {
                            display: false,
                        },
                        grid: {
                            display: false,
                        },
                        ticks: {
                            display: false,
                        },
                        offset: true,
                    },
                },
                plugins: {
                    tooltip: {
                        enabled: false,
                    },
                    legend: {
                        display: false,
                    },
                    datalabels: {
                        align(context: Context) {
                            const value = context.dataset.data[context.dataIndex] as ScatterPoint;
                            if (value.isCategoryHighestX) {
                                return 'left';
                            }
                            if (value.isCategoryHighestY) {
                                return 'start';
                            }
                            return 'bottom';
                        },
                        clamp: true,
                        clip: false,
                        anchor: 'center',
                        borderRadius: 15,
                        borderWidth(context: Context) {
                            return context.active ? 2 : 1;
                        },
                        borderColor(context: Context) {
                            const value = context.dataset.data[context.dataIndex] as ScatterPoint;
                            if (value.isCategoryHighestX || value.isCategoryHighestY || context.active) {
                                return value.borderColor;
                            }
                            return null;
                        },
                        color: 'white',
                        padding(context: Context) {
                            return context.active ? 5 : 6;
                        },
                        opacity(context: Context) {
                            return context.active ? 1 : 0.85;
                        },
                        font: {
                            weight: 'bold',
                            family: 'Moderat',
                            size: 13,
                        },
                        backgroundColor(context: Context) {
                            const value = context.dataset.data[context.dataIndex] as ScatterPoint;
                            return value.backgroundColor;
                        },
                        /**
                         * Point hover (Chart.js active) or priority points, else auto-hide overlaps.
                         * Hover detail panel is driven by `listeners.enter` / `listeners.leave` on labels.
                         */
                        display(context: Context) {
                            const value = context.dataset.data[context.dataIndex] as ScatterPoint;
                            return context.active || value.isCategoryHighestX || value.isCategoryHighestY ? true : 'auto';
                        },
                        formatter(value: ScatterPoint): string | null {
                            return value.displayName;
                        },
                        offset: 6,
                        listeners: {
                            enter(context: Context, event: ChartEvent) {
                                context.chart.setActiveElements([{datasetIndex: context.datasetIndex, index: context.dataIndex}]);
                                const elements = context.chart.getActiveElements();
                                cursorHandler('labelEnter', elements[0]);
                                tooltipHandler('labelEnter', elements[0], event.native as MouseEvent, context.chart);
                            },
                            leave(context: Context, event: ChartEvent) {
                                context.chart.setActiveElements([]);
                                tooltipHandler('labelLeave', undefined, event.native as MouseEvent, context.chart);
                                cursorHandler('labelLeave', undefined);
                            },
                            click(context: Context, event: ChartEvent) {
                                context.chart.setActiveElements([{datasetIndex: context.datasetIndex, index: context.dataIndex}]);
                                const elements = context.chart.getActiveElements();
                                tooltipHandler('labelClick', elements[0], event.native as MouseEvent, context.chart);
                            },
                        },
                    },
                    referenceLines: {
                        x: 1,
                        y: 400,
                        color: 'rgba(128, 128, 128, 0.5)',
                        lineWidth: 1,
                    },
                },
            },
        };

        function handleCloseTooltip() {
            isTooltipLockedByClick.value = false;
            isTooltipLockeByClickOnLabel.value = false;
            clearTooltip();
        }

        onMounted(() => {
            if (!chartRef.value) {
                return;
            }
            Chart.register(referenceLinesPlugin);
            const instance = new Chart(chartRef.value, chartConfig);
            chart.value = instance;
        });

        onUnmounted(() => {
            chart.value?.destroy();
            chart.value = null;
        });

        return {
            // a-z functions
            handleCloseTooltip,
            // a-z
            chart,
            chartConfig,
            chartRef,
        };
    }

    function useFilters(categoriesSorted: TrendsChartCategory[], chart: ShallowRef<Chart<'scatter'> | null>) {
        const categoryVisibility = ref(Object.fromEntries(categoriesSorted.map((c) => [c.id, true])) as Record<string, boolean>);
        const isFilterEnabled = computed(() => categoriesSorted.length > 1);

        const categoriesOptions = computed(() => {
            return categoriesSorted.map((category) => ({
                id: category.id,
                label: category.label,
                value: category.id,
                color: category.color,
            }));
        });
        const selectedCategories = computed<string[]>({
            get() {
                return Object.entries(categoryVisibility.value)
                    .filter(([_, value]) => value)
                    .map(([key]) => key);
            },
            set(value) {
                setVisibleCategories(value);
            },
        });

        function applyVisibility(nextVisibility: Record<string, boolean>) {
            categoriesSorted.forEach((category, index) => {
                chart.value?.setDatasetVisibility(index, nextVisibility[category.id] ?? false);
            });
            chart.value?.update();
            categoryVisibility.value = nextVisibility;
        }

        function setVisibleCategories(visibleCategories: string[]) {
            const selected = new Set(visibleCategories);
            applyVisibility(
                Object.fromEntries(categoriesSorted.map((category) => [category.id, selected.has(category.id)])) as Record<string, boolean>,
            );
        }

        function toggleCategory(category: string) {
            applyVisibility({
                ...categoryVisibility.value,
                [category]: !categoryVisibility.value[category],
            });
        }

        return {
            // a-z functions
            setVisibleCategories,
            toggleCategory,
            // a-z
            categoryVisibility,
            categoriesOptions,
            isFilterEnabled,
            selectedCategories,
        };
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .trends-chart {
        &__filter-bar {
            margin: var(--joy-core-spacing-4) 0;

            .joy-checkbox .joy-checkbox__content-wrapper {
                align-items: center;

                .trends-chart__category-dot {
                    margin-right: var(--joy-core-spacing-1);
                }
            }

            @include mq.screen_md(min) {
                display: none;
            }

            > * {
                flex: 1;
            }
        }

        &__filters {
            display: none;

            .joy-tag {
                border-radius: var(--joy-core-radius-5);
            }

            @include mq.screen_md(min) {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: space-between;
                gap: 22px;
                margin: 0 var(--joy-core-spacing-4);
            }
        }

        &__category-dot {
            width: 8px;
            height: 8px;
            border-radius: var(--joy-core-radius-6);
            aspect-ratio: 1 / 1;
        }

        &__canvas-wrapper {
            position: relative;
            width: 100%;
            max-height: calc(100vh - 250px);

            @include mq.screen_md(min) {
                margin-bottom: var(--joy-core-spacing-8);
            }

            @media (orientation: portrait) {
                height: 80vh;
                aspect-ratio: 1 / 2;
            }

            @media (orientation: landscape) {
                aspect-ratio: 2 / 1;
            }
        }
    }
</style>
